import { useCallback } from 'react'
import _ from 'lodash'
import { useFormDispatch, useFormSelector } from '../../../redux/formStoreHooks'
import L3DiagnosisAuditReview from '../l3/L3DiagnosisAuditReview'
import L3DiagnosisAuditReviewTrigger from '../l3/L3DiagnosisAuditReviewTrigger'
import { CodeNext } from '@fusion-advantage/backcompat'
import { DiagnosisReviewItem, diagnosisReviewItemDefaultAuditComment, diagnosisReviewItemIsPendingAuditComment } from '@fusion-advantage/formlib'
import Loading from '../../Loading'
import { commitDiagnosisReviewComment, selectDiagnosisReviewEditingComment, selectDiagnosisReviewAuditValidation, updateDiagnosisItemByIdx, selectDiagnosisReviewDisplayingComments, setDiagnosisReviewDisplayingComments, setDiagnosisReviewEditingComments, editDiagnosisReviewDisplayingComment, selectDiagnosisReviewSortedModels, selectDiagnosisReviewIsWritable, setSecondaryCodesLoading, setSecondaryCodes, removeAdhocDxIcd } from '../../../redux/diagnosisReviewSlice'
import { L3EditCommentDialog, L3ReadCommentDialog } from '../l3/L3CommentDialog'
import L4SecondaryCodingTable from './L4SecondaryCodingTable'
import { useFormAppRelay } from '../../../lib/relay/FormAppRelay'
import { selectExport } from '../../../redux/selectForm'
import { submitForm } from '../../../lib/persistence/submitForm'
import { FormModal, unshiftFormModal } from '../../../redux/formSlice'

const L4DiagnosisAuditReview = () => {
	const { querySecondaryCodes } = useFormAppRelay()
	const writable = useFormSelector(selectDiagnosisReviewIsWritable)
	const formLoading = useFormSelector(state => state.render.formLoading)
	const stateModels = useFormSelector(state => state.diagnosisReview.models)
	const sortedModels = useFormSelector(selectDiagnosisReviewSortedModels)
	const validation = useFormSelector(selectDiagnosisReviewAuditValidation)
	const comboCodesLoading = useFormSelector(state => state.diagnosisReview.secondaryCodesLoading)
	const displayingCommentsRef = useFormSelector(state => state.diagnosisReview.displayingComments)
	const displayingComments = useFormSelector(selectDiagnosisReviewDisplayingComments)
	const editingCommentRef = useFormSelector(state => state.diagnosisReview.editingComment)
	const editingComment = useFormSelector(selectDiagnosisReviewEditingComment)
	const encounterAppLastStatusDT = useFormSelector(state => selectExport(state)?.Form.Encounter.EncounterAppLastStatusDT)
	const dispatch = useFormDispatch()

	const searchComboCodes = useCallback(async () => {
		dispatch(setSecondaryCodesLoading(true))
		const result = await querySecondaryCodes({models: stateModels})
		dispatch(setSecondaryCodes(result?.secondaryCodes ?? []))
		dispatch(setSecondaryCodesLoading(false))
	}, [dispatch, stateModels])

	const canEditDisplayingComments = () => {
		if(writable && displayingCommentsRef !== null) {
			return true
		}
		return false
	}

	const handleChange = (modelIdx: number, updatedModel: DiagnosisReviewItem) => {
		if(!writable) return
		if(diagnosisReviewItemIsPendingAuditComment(updatedModel, encounterAppLastStatusDT)) {
			// don't submit a decision model change yet;
			// ask for a comment first
			dispatch(setDiagnosisReviewEditingComments({
				itemIdx: modelIdx,
				itemAuditNextStatus: updatedModel.auditDecision!,
				commentType: 'auditComments',
				commentIdx: 'new',
				defaultContent: diagnosisReviewItemDefaultAuditComment(updatedModel),
			}))
			return
		}
		dispatch(updateDiagnosisItemByIdx({
			idx: modelIdx,
			item: updatedModel,
		}))
	}

	const handleAuditCommentClick = (modelIdx: number) => {
		if(writable && stateModels[modelIdx].auditComments.length === 0) {
			dispatch(setDiagnosisReviewEditingComments({
				itemIdx: modelIdx,
				commentType: 'auditComments',
				commentIdx: 'new',
				defaultContent: diagnosisReviewItemDefaultAuditComment(stateModels[modelIdx]),
			}))
			return
		}
		dispatch(setDiagnosisReviewDisplayingComments({
			itemIdx: modelIdx,
			commentType: 'auditComments',
		}))
	}

	const handleReadCommentDialogClose = () => {
		dispatch(setDiagnosisReviewDisplayingComments(null))
	}

	const handleAuditCommentAdd = () => {
		dispatch(editDiagnosisReviewDisplayingComment('new'))
		// [ISSUE-485] Close comment box when confirm comment button is clicked
		handleReadCommentDialogClose()
	}

	const handleAuditCommentEdit = (commentIdx: number) => {
		dispatch(editDiagnosisReviewDisplayingComment(commentIdx))
		// [ISSUE-485] Close comment box when confirm comment button is clicked
		handleReadCommentDialogClose()
	}

	const handleAuditCommentSave = (message: string) => {
		const itemIdx = editingCommentRef!.itemIdx
		dispatch(commitDiagnosisReviewComment(message))
		dispatch(setDiagnosisReviewEditingComments(null))
		// [ISSUE-485] Close comment box when confirm comment button is clicked
		// dispatch(setDiagnosisReviewDisplayingComments({
		// 	itemIdx,
		// 	commentType: 'auditComments'
		// }))
	}

	const handleAddDiagnosisClick = () => {
		if(!writable) return
		dispatch(unshiftFormModal(FormModal.AddAdhocDiagnosis))
	}

	const handleTrashClick = (icd: string) => {
		if(!writable) return
		dispatch(removeAdhocDxIcd(icd))
	}

	return (
		<>
			<L3DiagnosisAuditReview
				models={sortedModels}
				onChange={handleChange}
				onAuditCommentClick={handleAuditCommentClick}
				onAddDiagnosisClick={handleAddDiagnosisClick}
				onTrashClick={handleTrashClick}
			/>
			{comboCodesLoading ? <Loading /> : null}
			<L4SecondaryCodingTable />
			<L3DiagnosisAuditReviewTrigger
				{...validation}
				onSubmit={() => dispatch(submitForm({codeNext: CodeNext.Audit1ToComplete}))}
				onSubmitToClinician={() => dispatch(submitForm({codeNext: CodeNext.ClinicianAuditReview}))}
				onRunComboCodes={searchComboCodes}
				loading={formLoading || comboCodesLoading}
			/>
			{displayingComments ? (
				<L3ReadCommentDialog
					title="Comments"
					show={true}
					comments={displayingComments}
					encounterAppLastStatusDT={encounterAppLastStatusDT}
					onAddClick={canEditDisplayingComments() ? handleAuditCommentAdd : undefined}
					onEditClick={canEditDisplayingComments() ? handleAuditCommentEdit : undefined }
					onClose={handleReadCommentDialogClose}
				/>
			) : null}
			{editingCommentRef ? (
				<L3EditCommentDialog
					title={editingCommentRef.commentIdx === 'new' ? 'Add Comment' : 'Edit Comment'}
					show={true}
					existingMessage={editingComment?.message ?? editingCommentRef.defaultContent}
					onSave={handleAuditCommentSave}
					onClose={() => dispatch(setDiagnosisReviewEditingComments(null))}
				/>
			) : null}
		</>
	)
}

export default L4DiagnosisAuditReview